"use client";

/**
 * QuoteForm — compact, embeddable lead-capture form for money/landing pages.
 *
 * WHY THIS EXISTS: the money pages (/water-damage-restoration-phoenix, etc.)
 * previously had NO inline form — the only form lived on /contact, one click
 * away, so every non-caller / after-hours paid-traffic visitor who preferred
 * to type was lost. This drops a working form directly onto the landing page.
 *
 * It deliberately reuses the SAME battle-tested plumbing as the full /contact
 * form so it inherits proven behavior and fires the same conversions:
 *   - POSTs to /api/dispatch (identical payload shape)
 *   - Cloudflare Turnstile bot challenge (<Turnstile>)
 *   - first-touch attribution via readAttribution() (carries gclid/utm)
 *   - fires GA4 `generate_lead` + Google Ads `conversion` (+ LSA) on success
 *   - redirects to /thank-you (stable Google Ads destination-URL conversion)
 *
 * Pass `defaultLossType` so each page pre-selects its service (water/fire/…).
 */

import { useState } from "react";
import { useRouter } from "next/navigation";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Send, CheckCircle2 } from "lucide-react";
import { BRAND } from "@/lib/brand";
import { readAttribution } from "@/lib/attribution";
import { Turnstile } from "@/components/security/turnstile";

const LOSS_OPTIONS = [
  { value: "water", label: "Water Damage" },
  { value: "fire", label: "Fire & Smoke" },
  { value: "mold", label: "Mold" },
  { value: "biohazard", label: "Biohazard / Trauma" },
  { value: "storm", label: "Storm / Flood" },
  { value: "other", label: "Other" },
] as const;

type LossType = (typeof LOSS_OPTIONS)[number]["value"];

// Mirrors the /contact schema so /api/dispatch accepts it identically.
const schema = z.object({
  name: z.string().min(2, "Please enter your name").max(120),
  phone: z.string().min(7, "Phone number looks too short").max(32),
  email: z.string().email("Enter a valid email"),
  address: z.string().min(3, "Please enter the property address").max(300),
  lossType: z.enum(["water", "fire", "mold", "biohazard", "storm", "other"]),
  message: z.string().max(4000).optional().or(z.literal("")),
  emergency: z.boolean(),
  // Honeypot — real users never see this.
  website: z.string().max(0, "caught").optional().or(z.literal("")),
});
type FormValues = z.infer<typeof schema>;

/** Progressive North-American phone formatting, matching the /contact form. */
function formatPhone(raw: string): string {
  if (!raw) return "";
  if (raw.trim().startsWith("+")) return raw;
  const digits = raw.replace(/\D/g, "");
  const ten = digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
  if (ten.length === 0) return "";
  if (ten.length < 4) return `(${ten}`;
  if (ten.length < 7) return `(${ten.slice(0, 3)}) ${ten.slice(3)}`;
  return `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6, 10)}`;
}

const fieldStyle: React.CSSProperties = {
  width: "100%",
  padding: "13px 15px",
  borderRadius: "12px",
  border: "1px solid rgba(255,255,255,0.22)",
  background: "rgba(255,255,255,0.06)",
  color: "#fff",
  fontSize: "16px",
  fontFamily: "inherit",
  outline: "none",
};

const labelStyle: React.CSSProperties = {
  display: "block",
  fontSize: "13px",
  fontWeight: 600,
  color: "rgba(255,255,255,0.85)",
  marginBottom: "6px",
};

export function QuoteForm({
  defaultLossType = "water",
  heading = "Get a Free Inspection",
  subheading = "Tell us what happened — a live dispatcher responds fast. No obligation.",
}: {
  defaultLossType?: LossType;
  heading?: string;
  subheading?: string;
}) {
  const router = useRouter();
  const [serverError, setServerError] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { lossType: defaultLossType, emergency: false, website: "" },
  });

  async function onSubmit(values: FormValues) {
    setServerError(null);
    try {
      const attribution = readAttribution();
      const res = await fetch("/api/dispatch", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...values, attribution, turnstileToken }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setServerError(
          data?.message ??
            `We couldn't deliver your request. Call ${BRAND.phone} for immediate response.`,
        );
        return;
      }
      // Fire the same conversions the /contact form fires so paid ads can
      // optimize toward leads. Analytics must never break the success path.
      try {
        const w = window as unknown as { gtag?: (...args: unknown[]) => void };
        if (typeof w.gtag === "function") {
          w.gtag("event", "generate_lead", {
            form_name: "DRR Quote Form",
            source: "website",
            service_type: values.lossType,
            emergency: values.emergency,
          });
          const adsId = process.env.NEXT_PUBLIC_GOOGLE_ADS_ID;
          const adsLabel = process.env.NEXT_PUBLIC_GOOGLE_ADS_LEAD_LABEL;
          if (adsId && adsLabel) {
            w.gtag("event", "conversion", {
              send_to: `${adsId}/${adsLabel}`,
              value: 500,
              currency: "USD",
            });
          }
          const lsaId = process.env.NEXT_PUBLIC_LSA_CONVERSION_ID;
          // send_to must be AW-XXXXXXXXXX/label; a malformed value silently
          // no-ops the conversion, so warn instead of dropping in silence.
          if (lsaId && /^AW-\d+\/[\w-]+$/.test(lsaId)) {
            w.gtag("event", "conversion", { send_to: lsaId, value: 500, currency: "USD" });
          } else if (lsaId) {
            console.warn("[lsa] NEXT_PUBLIC_LSA_CONVERSION_ID malformed (expected AW-XXXXXXXXXX/label); LSA conversion not sent:", lsaId);
          }
        }
      } catch {
        // swallow — analytics never blocks the lead
      }
      const lt = encodeURIComponent(values.lossType);
      const em = values.emergency ? "1" : "0";
      router.push(`/thank-you?lt=${lt}&em=${em}`);
    } catch {
      setServerError(`Network error. Call ${BRAND.phone} for immediate response.`);
    }
  }

  const phoneVal = watch("phone") ?? "";

  return (
    <div
      style={{
        maxWidth: "440px",
        width: "100%",
        background: "rgba(5,13,24,0.82)",
        backdropFilter: "blur(16px)",
        WebkitBackdropFilter: "blur(16px)",
        borderRadius: "20px",
        border: "1px solid rgba(255,255,255,0.18)",
        boxShadow: "0 8px 32px rgba(0,0,0,0.45)",
        padding: "clamp(22px, 4vw, 32px)",
        fontFamily: "'Plus Jakarta Sans', sans-serif",
      }}
    >
      <h2 style={{ color: "#fff", fontSize: "clamp(22px, 3vw, 28px)", fontWeight: 800, margin: "0 0 6px" }}>
        {heading}
      </h2>
      <p style={{ color: "rgba(255,255,255,0.7)", fontSize: "14px", margin: "0 0 18px", lineHeight: 1.5 }}>
        {subheading}
      </p>

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <div style={{ display: "grid", gap: "12px" }}>
          <div>
            <label htmlFor="qf-name" style={labelStyle}>Name</label>
            <input id="qf-name" style={fieldStyle} autoComplete="name" {...register("name")} />
            {errors.name && <p style={{ color: "#fca5a5", fontSize: "12px", marginTop: "4px" }}>{errors.name.message}</p>}
          </div>

          <div>
            <label htmlFor="qf-phone" style={labelStyle}>Phone</label>
            <input
              id="qf-phone"
              style={fieldStyle}
              inputMode="tel"
              autoComplete="tel"
              value={phoneVal}
              onChange={(e) => setValue("phone", formatPhone(e.target.value), { shouldValidate: true })}
              placeholder="Your best phone number"
            />
            {errors.phone && <p style={{ color: "#fca5a5", fontSize: "12px", marginTop: "4px" }}>{errors.phone.message}</p>}
          </div>

          <div>
            <label htmlFor="qf-email" style={labelStyle}>Email</label>
            <input id="qf-email" style={fieldStyle} type="email" autoComplete="email" {...register("email")} />
            {errors.email && <p style={{ color: "#fca5a5", fontSize: "12px", marginTop: "4px" }}>{errors.email.message}</p>}
          </div>

          <div>
            <label htmlFor="qf-address" style={labelStyle}>Property address</label>
            <input id="qf-address" style={fieldStyle} autoComplete="street-address" {...register("address")} />
            {errors.address && <p style={{ color: "#fca5a5", fontSize: "12px", marginTop: "4px" }}>{errors.address.message}</p>}
          </div>

          <div>
            <label htmlFor="qf-lossType" style={labelStyle}>Type of damage</label>
            <select id="qf-lossType" style={{ ...fieldStyle, appearance: "auto" }} {...register("lossType")}>
              {LOSS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value} style={{ color: "#000" }}>{o.label}</option>
              ))}
            </select>
          </div>

          <label style={{ display: "flex", alignItems: "center", gap: "8px", color: "rgba(255,255,255,0.85)", fontSize: "14px", cursor: "pointer" }}>
            <input type="checkbox" {...register("emergency")} style={{ width: "18px", height: "18px" }} />
            This is an emergency — call me now
          </label>

          {/* Honeypot — visually hidden, not tab-reachable */}
          <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", width: "1px", height: "1px", overflow: "hidden" }}>
            <label htmlFor="qf-website">Website</label>
            <input id="qf-website" tabIndex={-1} autoComplete="off" {...register("website")} />
          </div>

          <Turnstile onToken={setTurnstileToken} theme="dark" />

          {serverError && (
            <p style={{ color: "#fca5a5", fontSize: "13px", margin: 0 }}>{serverError}</p>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            style={{
              display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "10px",
              background: "#DC2626", color: "#fff", fontWeight: 800, fontSize: "17px",
              padding: "16px 24px", borderRadius: "100px", border: "none", cursor: isSubmitting ? "wait" : "pointer",
              width: "100%", opacity: isSubmitting ? 0.7 : 1,
            }}
          >
            {isSubmitting ? "Sending…" : (<><Send size={18} /> Request My Inspection</>)}
          </button>

          <p style={{ color: "rgba(255,255,255,0.6)", fontSize: "12px", margin: "2px 0 0", textAlign: "center", display: "flex", alignItems: "center", justifyContent: "center", gap: "6px" }}>
            <CheckCircle2 size={13} /> Prefer to talk now? Call{" "}
            <a href={BRAND.phoneHref} style={{ color: "#fff", fontWeight: 700 }}>{BRAND.phone}</a>
          </p>
        </div>
      </form>
    </div>
  );
}
